import React, { useState } from 'react';
import './App.css';

// Fallback to local if env var not set
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/upload';

function App() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      // Basic validation
      if (selected.size > 2 * 1024 * 1024) {
        setError('File size must be less than 2MB');
        setFile(null);
        return;
      }
      setFile(selected);
      setError(null);
      setResultUrl(null);
    }
  };

  const toBase64 = async (file: File): Promise<string> => {
    if (typeof FileReader !== 'undefined') {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
    }

    if (typeof btoa === 'undefined') {
      throw new Error('Your browser does not support file conversion');
    }

    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      for (let j = 0; j < chunk.length; j += 1) {
        binary += String.fromCharCode(chunk[j]);
      }
    }

    const mimeType = file.type || 'application/octet-stream';
    return `data:${mimeType};base64,${btoa(binary)}`;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setResultUrl(null);

    try {
      const base64Content = await toBase64(file);

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: base64Content, // Keep the data:URI scheme prefix logic in backend
          filename: file.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setResultUrl(data.download_url);
      setRemaining(data.remaining_uploads);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'An error occurred during conversion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Free PDF Converter</h1>
        <p>Convert your documents to PDF.</p>
        <p className="limit-info">Limit: 5 uploads per day per IP. Demo use only.</p>
      </header>

      <main className="App-main">
        <div className="upload-container">
          <input
            type="file"
            onChange={handleFileChange}
            accept=".docx,.txt,.pptx,.jpg,.png,.md"
            className="file-input"
          />

          <button onClick={handleUpload} disabled={!file || loading} className="upload-btn">
            {loading ? 'Converting...' : 'Convert to PDF'}
          </button>
        </div>

        {error && <div className="alert error">{error}</div>}

        {resultUrl && (
          <div className="alert success">
            <p>Conversion Successful!</p>
            <a href={resultUrl} target="_blank" rel="noopener noreferrer" className="download-link">
              Download PDF
            </a>
            {remaining !== null && <p className="small">Remaining uploads today: {remaining}</p>}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
